/**
 * Customer-editable mutations for an external suite's runner config.
 *
 * Split from `useTestSuites` because the two PATCH surfaces are a privilege
 * The superadmin-only runner-command surface remains available in the API for
 * operational compatibility, but is no longer exposed in the product UI.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { isExpectedClientApiError, toApiError } from '../../lib/api-error';
import type { ExternalCapturePolicy, ExternalRetryGranularity, SuiteExternalRunner } from './types';

const json = { 'content-type': 'application/json' };

/** Customer-editable runner fields. */
export interface UpdateExternalRunnerInput {
  propertyGithubRepoId?: string;
  repoRef?: string | null;
  targetEnvironmentId?: string | null;
  credentialId?: string | null;
  /**
   * Sent WHOLE. This is a replace, not a merge — removing a key here removes it
   * from the container's `.env`, which is also removing it from the injection
   * allowlist. Callers must send the complete map every time.
   */
  envMapping?: Record<string, string>;
  capturePolicy?: ExternalCapturePolicy;
  retryGranularity?: ExternalRetryGranularity;
  maxRetries?: number;
  /** Null clears the sandbox target; all three fields are set/cleared together. */
  sandboxTemplateId?: string | null;
  sandboxActionSlug?: string | null;
  sandboxActionParams?: Record<string, string | number | boolean> | null;
}

export interface UpdateExternalRunnerAdvancedInput {
  toolchainImage?: string;
  installCmd?: string | null;
  listCmd?: string | null;
  runCmd?: string;
  defaultSpecPath?: string | null;
  shardCmdTemplate?: string | null;
  artifactGlobs?: string[] | null;
  shardCount?: number | null;
  shardTimeoutMs?: number | null;
}

/** Customer-editable: the runner's configured directory areas. */
export interface UpdateExternalRunnerAreasInput {
  /** `[]` clears directory mode — never `null`. Lines may be glob patterns. */
  areaPaths: string[];
  /**
   * Stripped from each pattern before matching the repository, added back to
   * each resolved match. `null` clears it.
   */
  generatedPrefix: string | null;
}

/** One area as resolved by the areas preview endpoint. */
export interface ExternalRunnerAreaPreview {
  path: string;
  label: string;
  disabled: boolean;
}

export interface ExternalRunnerAreasPreview {
  /** The commit the patterns were resolved at; null for a literal-only config (no GitHub call). */
  commitSha: string | null;
  areas: ExternalRunnerAreaPreview[];
  /** Patterns that matched no directory in the repository. */
  unmatchedPatterns: string[];
}

/** Invalidate both the list card and the config page after a runner write. */
function useRunnerInvalidation(suiteId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['test-suites'] });
    void qc.invalidateQueries({ queryKey: ['test-suite', suiteId] });
  };
}

export function useUpdateExternalRunner(suiteId: string) {
  const invalidate = useRunnerInvalidation(suiteId);
  return useMutation({
    mutationFn: async (input: UpdateExternalRunnerInput): Promise<SuiteExternalRunner> => {
      const res = await fetch(`${API_URL}/test-suites/${suiteId}/external-runner`, {
        method: 'PATCH',
        credentials: 'include',
        headers: json,
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to save runner configuration');
      const body = (await res.json()) as { ok: boolean; data: SuiteExternalRunner };
      return body.data;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateExternalRunnerAdvanced(suiteId: string) {
  const invalidate = useRunnerInvalidation(suiteId);
  return useMutation({
    mutationFn: async (input: UpdateExternalRunnerAdvancedInput): Promise<SuiteExternalRunner> => {
      const res = await fetch(`${API_URL}/test-suites/${suiteId}/external-runner/advanced`, {
        method: 'PATCH',
        credentials: 'include',
        headers: json,
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to save advanced runner configuration');
      const body = (await res.json()) as { ok: boolean; data: SuiteExternalRunner };
      return body.data;
    },
    onSuccess: invalidate,
  });
}

/**
 * Set (or clear) the runner's whole configured area list.
 *
 * Distinct from `useSetAreaDisabled`: this rewrites which paths exist as
 * areas at all; that one only flips a path in/out of the `disabledAreas`
 * exclusion set. Open to any org member — directory areas are ordinary
 * suite configuration, not a superadmin surface.
 */
export function useUpdateExternalRunnerAreas(suiteId: string) {
  const invalidate = useRunnerInvalidation(suiteId);
  return useMutation({
    mutationFn: async (input: UpdateExternalRunnerAreasInput): Promise<SuiteExternalRunner> => {
      const res = await fetch(`${API_URL}/test-suites/${suiteId}/external-runner/areas`, {
        method: 'PATCH',
        credentials: 'include',
        headers: json,
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to save area directories');
      const body = (await res.json()) as { ok: boolean; data: SuiteExternalRunner };
      return body.data;
    },
    onSuccess: invalidate,
  });
}

/**
 * Resolve an area configuration (which may include glob patterns) against
 * the suite's repository, without saving it.
 *
 * A `useQuery`, not a mutation: the result is a pure function of
 * `(suiteId, areaPaths, generatedPrefix)` — the same config always resolves
 * to the same preview at a given commit, so React Query's cache/dedupe/
 * retry machinery applies directly, and callers get `isLoading`/`isError`
 * for free instead of tracking mutation state by hand. Callers debounce the
 * *inputs* (see `useDebouncedValue`) rather than the query itself, so typing
 * doesn't spam the resolution endpoint (which may call GitHub).
 */
export function usePreviewExternalRunnerAreas(
  suiteId: string,
  areaPaths: string[],
  generatedPrefix: string | null,
  options: { enabled: boolean }
) {
  return useQuery({
    queryKey: ['external-runner-areas-preview', suiteId, areaPaths, generatedPrefix],
    queryFn: async (): Promise<ExternalRunnerAreasPreview> => {
      const res = await fetch(`${API_URL}/test-suites/${suiteId}/external-runner/areas/preview`, {
        method: 'POST',
        credentials: 'include',
        headers: json,
        body: JSON.stringify({ areaPaths, generatedPrefix }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to preview area directories');
      const body = (await res.json()) as { ok: boolean; data: ExternalRunnerAreasPreview };
      return body.data;
    },
    enabled: options.enabled && !!suiteId,
    staleTime: 30_000,
    // Client errors (4xx / user errors, e.g. EXTERNAL_AREAS_PATTERN_INVALID)
    // are never transient — retrying just delays the error banner. Server
    // errors (5xx) may be transient, so allow a single retry.
    retry: (failureCount, error) => !isExpectedClientApiError(error) && failureCount < 1,
  });
}
